import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { type Task, localAnswer } from "../../content/lesson";
import { SakuraCoinIcon, SparkleStarIcon } from "../animation/AnimatedUIIcon";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
};

type ReviewResult = {
  total: number;
  correct_count: number;
  xp_awarded: number;
  coin_awarded: number;
  results: Array<{ task_id: string; is_correct: boolean; correct_answer: string }>;
};

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

export function ReviewParcelModal({ isOpen, onClose, tasks }: Props) {
  const { t, i18n } = useTranslation(["common", "learner"]);
  const isJa = i18n.language === "ja";
  const queryClient = useQueryClient();

  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ is_correct: boolean; correct_answer: string } | null>(null);
  const [answers, setAnswers] = useState<Array<{ task_id: string; selected: string }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ReviewResult | null>(null);

  const task = tasks[index];

  const choices = useMemo(() => {
    if (!task || task.type === "word_order") return [];
    return shuffle(task.choices);
  }, [task]);

  function handleCheck() {
    if (!task || !selected || feedback) return;
    const ok = selected.trim() === localAnswer(task).trim();
    setFeedback({ is_correct: ok, correct_answer: localAnswer(task) });
    setAnswers((prev) => [...prev, { task_id: task.id, selected }]);
  }

  async function handleNext() {
    if (index + 1 < tasks.length) {
      setIndex((i) => i + 1);
      setSelected(null);
      setFeedback(null);
    } else {
      // 最終問題提出
      setSubmitting(true);
      try {
        const res = await api<ReviewResult>("/api/learner/review/submit", {
          method: "POST",
          body: { answers },
        });
        setResult(res);
        void queryClient.invalidateQueries({ queryKey: ["profile"] });
        void queryClient.invalidateQueries({ queryKey: ["reviewTasks"] });
      } catch (err) {
        console.error("Review submit error:", err);
      } finally {
        setSubmitting(false);
      }
    }
  }

  function handleFinish() {
    setIndex(0);
    setSelected(null);
    setFeedback(null);
    setAnswers([]);
    setResult(null);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 15 }}
        className="bg-[#fffdf8] rounded-3xl shadow-2xl border-2 border-[#d9b45f]/50 max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* モーダルヘッダー */}
        <div className="bg-gradient-to-r from-[#ffeacc] via-[#fff5e6] to-[#fdede0] px-5 py-4 border-b border-[#d9b45f]/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl animate-bounce">📦</span>
            <div>
              <h2 className="text-base sm:text-lg font-black text-[#8f332c] leading-tight">
                {isJa ? "島に届いた復習便" : "Island Review Parcel"}
              </h2>
              <p className="text-[11px] text-stone-500 font-bold">
                {isJa
                  ? "前に間違えた問題をもう一度使ってみよう！🐾"
                  : "Let's master the words you had trouble with!🐾"}
              </p>
            </div>
          </div>
          <button
            onClick={handleFinish}
            className="w-8 h-8 rounded-full bg-white/80 hover:bg-white text-stone-600 flex items-center justify-center font-bold text-sm shadow-xs transition"
          >
            ✕
          </button>
        </div>

        {/* 完了画面（小包開封演出） */}
        {result ? (
          <div className="p-6 sm:p-8 flex flex-col items-center text-center overflow-y-auto">
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", damping: 12 }}
              className="text-6xl mb-3"
            >
              🎁
            </motion.div>
            <h3 className="text-xl sm:text-2xl font-black text-[#273f7a] mb-1">
              {isJa ? "復習便を開封しました！✨" : "Parcel Opened!✨"}
            </h3>
            <p className="text-xs sm:text-sm text-stone-600 mb-5 font-bold">
              {isJa
                ? `正解した問題はバッチリ身についたよ！（${result.correct_count}/${result.total} 問正解）`
                : `You mastered the words! (${result.correct_count}/${result.total} correct)`}
            </p>

            {/* 獲得報酬 */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-xs mb-6">
              <div className="bg-white rounded-2xl p-3 border border-emerald-200 shadow-sm flex flex-col items-center">
                <div className="flex items-center gap-1 text-[11px] text-stone-500 font-bold mb-0.5">
                  <SparkleStarIcon size={16} />
                  <span>{isJa ? "苦手克服" : "Mastered"}</span>
                </div>
                <span className="text-lg font-black text-emerald-600">
                  {result.correct_count} / {result.total}
                </span>
              </div>
              <div className="bg-white rounded-2xl p-3 border border-pink-200 shadow-sm flex flex-col items-center">
                <div className="flex items-center gap-1 text-[11px] text-stone-500 font-bold mb-0.5">
                  <SakuraCoinIcon size={16} />
                  <span>{isJa ? "桜コイン" : "Sakura Coins"}</span>
                </div>
                <span className="text-lg font-black text-pink-600">+{result.coin_awarded}</span>
              </div>
            </div>

            <button
              onClick={handleFinish}
              className="w-full max-w-xs bg-primary text-white font-black py-3 rounded-2xl shadow-lg hover:bg-primary/90 transition active:scale-98"
            >
              {isJa ? "受け取ってマップへ戻る" : "Claim & Back to Map"}
            </button>
          </div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center text-stone-500 font-bold">
            <p className="text-3xl mb-2">🎉</p>
            <p>{isJa ? "いま苦手な問題はありません！バッチリやで！" : "No review tasks right now! Great job!"}</p>
            <button
              onClick={handleFinish}
              className="mt-5 bg-stone-100 px-6 py-2 rounded-xl text-stone-700 font-bold"
            >
              {t("common:close")}
            </button>
          </div>
        ) : task ? (
          /* クイズ進行画面 */
          <div className="p-5 sm:p-6 flex-1 overflow-y-auto flex flex-col">
            {/* 進捗プログレスバー */}
            <div className="flex items-center justify-between text-xs font-bold text-stone-500 mb-2">
              <span>{index + 1} / {tasks.length}</span>
              <span>{task.type === "vocab" ? "ことば" : "文のいみ"}</span>
            </div>
            <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden mb-5">
              <div
                className="h-full bg-amber-500 transition-all duration-300"
                style={{ width: `${((index + 1) / tasks.length) * 100}%` }}
              />
            </div>

            {/* 問題カード */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200 mb-5 text-center">
              {task.type === "vocab" && (
                <>
                  <div className="text-3xl sm:text-4xl font-black text-primary mb-1">
                    {task.word}
                  </div>
                  {task.reading && (
                    <div className="text-sm text-stone-500 font-bold">
                      {task.reading}
                    </div>
                  )}
                </>
              )}
              {task.type === "translate" && (
                <div className="text-xl sm:text-2xl font-black text-primary leading-snug">
                  {task.sentence}
                </div>
              )}
              {task.type === "fill_blank" && (
                <div className="text-xl sm:text-2xl font-black text-primary leading-snug">
                  <span>{task.before}</span>
                  <span className="border-b-2 border-primary px-2 mx-1 text-amber-600">
                    {selected || "___"}
                  </span>
                  <span>{task.after}</span>
                </div>
              )}
              {task.type === "word_order" && (
                <div className="text-lg sm:text-xl font-black text-primary leading-snug">
                  {task.prompt}
                </div>
              )}
            </div>

            {/* 選択肢 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5 flex-1">
              {choices.map((c) => {
                const isSel = selected === c;
                const isAns = c === localAnswer(task);
                let cls = "border-stone-200 bg-white text-stone-800 hover:border-primary/60";
                if (feedback) {
                  if (isAns) cls = "border-green-500 bg-green-50 text-green-900 font-black shadow-xs";
                  else if (isSel) cls = "border-orange-400 bg-orange-50 text-orange-900";
                  else cls = "border-stone-200 opacity-50";
                } else if (isSel) {
                  cls = "border-primary bg-primary/10 text-primary font-black shadow-xs";
                }

                return (
                  <button
                    key={c}
                    type="button"
                    disabled={!!feedback}
                    onClick={() => setSelected(c)}
                    className={`border-2 rounded-xl p-3.5 text-sm sm:text-base font-bold text-left transition ${cls}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>

            {/* ボトム操作 */}
            <div className="pt-2">
              {feedback ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-black">
                    {feedback.is_correct ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <span>✨</span>
                        <span>正解！バッチリや！</span>
                      </span>
                    ) : (
                      <span className="text-orange-600">
                        正解: <strong>{feedback.correct_answer}</strong>
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={submitting}
                    className="bg-primary text-white font-black px-6 py-2.5 rounded-xl shadow hover:bg-primary/90 transition active:scale-95 text-sm"
                  >
                    {submitting
                      ? "採点中..."
                      : index + 1 < tasks.length
                        ? "次へ ➔"
                        : "結果を見る 🎁"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleCheck}
                  disabled={!selected}
                  className={`w-full py-3 rounded-xl font-black text-base transition shadow ${selected
                      ? "bg-primary text-white hover:bg-primary/90 active:scale-98"
                      : "bg-stone-200 text-stone-400 cursor-not-allowed"
                    }`}
                >
                  答え合わせ
                </button>
              )}
            </div>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}
